import { getTheme, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { DocumentSearchIcon } from "@fluentui/react-icons-mdl2";
import { useEffect, useState } from "react";

import { Tariff } from "../interfaces/invoices-response";

import { Card } from "@/components/Shared/Card";
import { useApi } from "@/core/libs/api/react-query/useApi";

interface MainContentProps {
  selectedTariff?: Tariff;
  isLoading?: boolean;
  invoiceId?: number;
  document?: string;
  expandedInvoices?: number[];
}

const theme = getTheme();

const LoadingInvoiceCard = () => {
  return (
    <Card
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Spinner size={SpinnerSize.large} label="Carregando..." />
    </Card>
  );
};

const EmptyInvoiceCard = () => {
  return (
    <Card
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        gap: "1rem"
      }}
    >
      <DocumentSearchIcon
        style={{
          fontSize: theme.fonts.mega.fontSize
        }}
      />
      <p
        style={{
          fontSize: theme.fonts.large.fontSize
        }}
      >
        Selecione a fatura e a tarifa ao lado para visualizar o PDF do
        documento.
      </p>
    </Card>
  );
};

export default function PdfViewer({
  selectedTariff,
  isLoading = false,
  invoiceId,
  document,
  expandedInvoices = []
}: MainContentProps) {
  const { execute } = useApi();

  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [hasPdfError, setHasPdfError] = useState<boolean>(false);

  useEffect(() => {
    let pdfObjectUrl: string = null;
    const controller = new AbortController();
    const { signal } = controller;

    const loadPdf = async () => {
      setPdfLoading(true);
      setHasPdfError(false);
      setPdfUrl("");

      try {
        const isTariff = Boolean(selectedTariff);

        const url = isTariff
          ? `invoices/${invoiceId}/tariff-pdf/${selectedTariff.id}?document=${document}`
          : `invoices/${invoiceId}/pdf?document=${document}`;

        const response = await execute<ArrayBuffer>({
          url,
          method: "GET"
        });

        if (signal.aborted) return;

        if (pdfObjectUrl) {
          URL.revokeObjectURL(pdfObjectUrl);
          pdfObjectUrl = null;
        }

        const blob = new Blob([response.data], { type: "application/pdf" });
        pdfObjectUrl = URL.createObjectURL(blob);
        setPdfUrl(pdfObjectUrl);
      } catch {
        if (signal.aborted) return;

        setHasPdfError(true);
      } finally {
        if (!signal.aborted) {
          setPdfLoading(false);
        }
      }
    };

    const shouldFetchPdf =
      invoiceId &&
      document &&
      (selectedTariff || expandedInvoices?.length > 0) &&
      (!selectedTariff || selectedTariff.invoiceId === invoiceId);

    if (!shouldFetchPdf) {
      setPdfUrl("");
      setPdfLoading(false);
      setHasPdfError(false);

      return;
    }

    void loadPdf();

    return () => {
      controller.abort();

      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [invoiceId, document, selectedTariff, expandedInvoices, execute]);

  const showLoading = isLoading || pdfLoading;
  const showPdf = pdfUrl && !hasPdfError;

  return (
    <Stack
      styles={{
        root: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }
      }}
    >
      {showLoading && <LoadingInvoiceCard />}

      {!showLoading && showPdf && (
        <iframe
          style={{
            width: "100%",
            height: "100%",
            border: "none"
          }}
          src={pdfUrl}
          title={
            selectedTariff
              ? `Tarifa - ${selectedTariff.identifier}`
              : "PDF - Fatura Resumida"
          }
        />
      )}

      {!showLoading && !showPdf && <EmptyInvoiceCard />}
    </Stack>
  );
}
